import { authorizedFetch, json } from '@nimbitlabs/ui';

export interface ServiceNode {
  id: string;
  name: string;
  type: 'service' | 'database' | 'external';
  status: 'healthy' | 'degraded' | 'down';
  environmentId: string;
}

export interface ServiceEdge {
  source: string;
  target: string;
  type: 'api' | 'database' | 'message-queue' | 'cache';
  edgeCategory: 'dependency' | 'dataflow';
  latency?: number;
}

export interface DependencyGraph {
  nodes: ServiceNode[];
  edges: ServiceEdge[];
}

export interface ImpactAnalysis {
  serviceId: string;
  serviceName: string;
  directDependents: string[];
  indirectDependents: string[];
  totalImpact: number;
}

import type { ServiceDependencyDto, CreateServiceDependencyDto, UpdateServiceDependencyDto } from '@/types/api';

export const dependenciesApi = {
  async getDependencyGraph(environmentId?: string): Promise<DependencyGraph> {
    const params = environmentId ? `?environmentId=${environmentId}` : '';
    const res = await authorizedFetch(`/dependencies/graph${params}`);
    return json<DependencyGraph>(res);
  },

  async getImpactAnalysis(serviceId: string): Promise<ImpactAnalysis> {
    const res = await authorizedFetch(`/dependencies/service/${serviceId}/impact`);
    return json<ImpactAnalysis>(res);
  },

  async getServiceDependencies(serviceId: string): Promise<ServiceDependencyDto[]> {
    const res = await authorizedFetch(`/dependencies/service/${serviceId}`);
    return json<ServiceDependencyDto[]>(res);
  },

  async createDependency(dto: CreateServiceDependencyDto): Promise<ServiceDependencyDto> {
    const res = await authorizedFetch('/dependencies', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
    return json<ServiceDependencyDto>(res);
  },

  async updateDependency(id: string, dto: UpdateServiceDependencyDto): Promise<ServiceDependencyDto> {
    const res = await authorizedFetch(`/dependencies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dto),
    });
    return json<ServiceDependencyDto>(res);
  },

  async deleteDependency(id: string): Promise<void> {
    await authorizedFetch(`/dependencies/${id}`, {
      method: 'DELETE',
    });
  },

  // Legacy method for dependency graph - kept for backward compatibility
  async addDependency(
    source: string,
    target: string,
    type: ServiceEdge['type'],
    edgeCategory: ServiceEdge['edgeCategory'] = 'dependency'
  ): Promise<void> {
    await authorizedFetch('/dependencies', {
      method: 'POST',
      body: JSON.stringify({ 
        serviceId: source, 
        dependsOnServiceId: target, 
        dependencyType: type,
        isCritical: edgeCategory === 'dependency'
      }),
    });
  },

  async removeDependency(id: string): Promise<void> {
    await this.deleteDependency(id);
  },
};
